import React, { useEffect, useState } from "react";
import { navigate } from "gatsby";
import * as prismic from "@prismicio/client";
import { linkResolver } from "src/lib/linkResolver";

/**
 * Preview page for Prismic CMS
 *
 * This page is called by Prismic when a user clicks "Preview" in the CMS.
 * It receives a preview token, fetches the preview document, resolves its URL,
 * and redirects to the appropriate page.
 *
 * Configure this URL in Prismic Settings → Previews:
 * - Local: http://localhost:8000/preview
 * - Netlify: https://[your-site].netlify.app/preview
 */
const PreviewPage: React.FC = () => {
    const [status, setStatus] = useState<string>("Initializing preview...");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handlePreview = async () => {
            try {
                // Get the preview token from URL query parameters
                const params = new URLSearchParams(window.location.search);
                const token = params.get("token");

                if (!token) {
                    throw new Error("No preview token found in URL");
                }

                setStatus("Fetching preview document...");

                // Get repository configuration from environment
                const repositoryName = process.env.GATSBY_PRISMIC_REPOSITORY;
                const accessToken = process.env.GATSBY_PRISMIC_TOKEN;

                if (!repositoryName) {
                    throw new Error("GATSBY_PRISMIC_REPOSITORY is not configured");
                }

                // Create Prismic client
                const endpoint = prismic.getRepositoryEndpoint(repositoryName);
                const client = prismic.createClient(endpoint, {
                    accessToken: accessToken || undefined,
                });

                // Resolve the preview URL using linkResolver
                const previewResolver = await client.resolvePreviewURL({
                    linkResolver,
                    defaultURL: "/",
                });

                setStatus("Redirecting to preview page...");

                // Navigate to the resolved URL
                if (previewResolver) {
                    await navigate(previewResolver);
                } else {
                    console.warn("[Preview] No URL resolved, redirecting to homepage");
                    await navigate("/");
                }
            } catch (err) {
                console.error("[Preview] Error:", err);
                const errorMessage = err instanceof Error ? err.message : String(err);
                setError(errorMessage);
                setStatus("Preview failed");

                // Redirect to homepage after 3 seconds on error
                setTimeout(() => {
                    navigate("/");
                }, 3000);
            }
        };

        handlePreview();
    }, []);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
            }}
        >
            <div style={{ maxWidth: "600px", textAlign: "center" }}>
                <h1 style={{ fontSize: "24px", marginBottom: "16px" }}>
                    Prismic Preview
                </h1>

                <p style={{ fontSize: "16px", color: "#666", marginBottom: "24px" }}>
                    {status}
                </p>

                {error && (
                    <div
                        style={{
                            padding: "16px",
                            backgroundColor: "#fee",
                            border: "1px solid #fcc",
                            borderRadius: "8px",
                            marginTop: "16px",
                        }}
                    >
                        <p style={{ color: "#c00", margin: 0 }}>
                            <strong>Error:</strong> {error}
                        </p>
                        <p style={{ color: "#666", fontSize: "14px", marginTop: "8px" }}>
                            Redirecting to homepage in 3 seconds...
                        </p>
                    </div>
                )}

                {!error && (
                    <div
                        style={{
                            display: "inline-block",
                            width: "40px",
                            height: "40px",
                            border: "4px solid #f3f3f3",
                            borderTop: "4px solid #3498db",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                        }}
                    />
                )}
            </div>

            <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default PreviewPage;
